// BattlemapsPacks.js
// The Environment Pack store: find and purchase Environment Packs.
//
// This is deliberately NOT the D&D / Professional search. Those search
// generated slugs, because those product lines resolve to very large numbers of
// generator families. Battlemaps Environment Packs are a controlled set of
// purchasable catalogue products, so the search here matches PACK PRODUCTS and
// their metadata - name, environment group, descriptors - and returns packs.
//
// The five-engine unlock and the included Creation Credits are explained ONCE at
// the top rather than repeated inside all the cards; the cards then carry only
// what actually differs between them.
//
// Where the catalogue comes from (2026-08-25 pack-pipeline audit)
// ---------------------------------------------------------------------------
// This page used to render a hard-coded array in battlemapsCatalog.js, which
// meant publishing a new Environment Pack required editing React source. The
// catalogue now comes from GET /v1/battlemaps/packs, which the two-BAT
// production pipeline refreshes automatically, so a pack published by BAT 2
// appears here on its own. BATTLEMAPS_PACKS stays as the render-before-fetch
// fallback - the same pattern battlemapsCommerce.js already uses for prices -
// so the store is never blank while the request is in flight.
//
// The whole catalogue is fetched once and filtered in the browser. That keeps
// the instant-feedback search behaviour exactly as it was, and the catalogue is
// a few hundred pack records - it is the child generators, tens of thousands of
// them, that must never be searched as store products.
import React, { useMemo, useState } from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import {
  useBattlemapsCommerce, useBattlemapsAccount, useBattlemapsPacks, formatPrice,
} from './battlemapsCommerce';

const AMBER = '#d4a72c';
const CARD_BG = 'linear-gradient(158deg, rgba(212,167,44,0.07) 0%, rgba(212,167,44,0.02) 60%, rgba(255,255,255,0.012) 100%), var(--vi-s1, #15141c)';
const CARD_BORDER = '1px solid rgba(212,167,44,0.22)';

// Extra search terms per pack, derived from the pack's own identity. Searching
// "haunted" or "snow" has to reach the right pack even though those words are
// only part of a longer product name.
function searchTerms(pack) {
  return [
    pack.name, pack.id.replace(/-/g, ' '), pack.categoryName, pack.category,
    ...(pack.aliases || []),
    ...(pack.groups || []),
  ].join(' ').toLowerCase();
}

export default function BattlemapsPacks() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const { commerce } = useBattlemapsCommerce();
  const { ownedSlugs, creationCredits } = useBattlemapsAccount();
  const { packs } = useBattlemapsPacks();
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('all');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const env = commerce.environment_pack;

  // The catalogue is whatever the pipeline last published; useBattlemapsPacks
  // serves the approved fallback until it arrives, so the store is never blank.
  const INDEXED = useMemo(
    () => packs.map((p) => ({ ...p, _terms: searchTerms(p) })),
    [packs],
  );

  // Groups come from the catalogue itself. Only groups that actually exist are
  // offered - an empty filter chip for an environment with no packs would be a
  // promise the catalogue cannot keep. A group is browse metadata: it filters
  // the store, it is never itself a product.
  const groups = useMemo(() => {
    const seen = new Map();
    INDEXED.forEach((p) => {
      if (!seen.has(p.category)) seen.set(p.category, { id: p.category, name: p.categoryName, count: 0 });
      seen.get(p.category).count += 1;
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [INDEXED]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const words = q ? q.split(/\s+/) : [];
    return INDEXED.filter((p) => {
      if (group !== 'all' && p.category !== group) return false;
      if (!words.length) return true;
      return words.every((w) => p._terms.includes(w));
    });
  }, [INDEXED, query, group]);

  // Results stay grouped so a broad search ("forest") reads as an environment
  // group with its packs under it, rather than 19 undifferentiated cards.
  const grouped = useMemo(() => {
    const out = [];
    const byCat = new Map();
    results.forEach((p) => {
      if (!byCat.has(p.category)) { byCat.set(p.category, []); out.push({ id: p.category, name: p.categoryName, packs: byCat.get(p.category) }); }
      byCat.get(p.category).push(p);
    });
    out.forEach((g) => { g.packs = byCat.get(g.id); });
    return out;
  }, [results]);

  const purchase = async (slug) => {
    setBusy(slug); setError('');
    try {
      const r = await consentCheckout.start(() => API.post(`/v1/battlemaps/packs/${slug}/checkout`), { purchaseType: 'battlemap_environment_pack', intendedSelector: slug });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const d = err.response && err.response.data;
      setError((d && (d.message || d.error)) || 'Checkout could not be started. Please try again.');
      setBusy(null);
    }
  };

  return (
    <>
      <ProductNav />
      <div style={s.page}>
      <div style={s.root}>
      {/* ---- Explained once, not inside every card ---- */}
      <div style={s.intro}>
        <div style={s.eyebrow}>Battlemaps</div>
        <h1 style={s.h1}>Environment Packs</h1>
        <p style={s.introBody}>
          Every Environment Pack is a one-time {formatPrice(env.price_usd, commerce.currency)} purchase
          with permanent ownership. It unlocks that environment across all five Battlemaps creation
          systems &mdash; {env.unlocks_engines.join(', ')} &mdash; and includes{' '}
          <strong style={s.strong}>{env.included_credits} Creation Credits</strong>.
        </p>
        <div style={s.introMetaRow}>
          <span style={s.metaPill}>{INDEXED.length} packs available</span>
          <span style={s.metaPill}>{ownedSlugs.size} owned</span>
          <span style={s.metaPill}>{creationCredits} Creation Credits</span>
          <button type="button" style={s.linkBtn} onClick={() => navigate('/battlemaps/pricing')}>
            How pricing works
          </button>
        </div>
      </div>

      {/* ---- Navigation: one prominent search + compact group controls ---- */}
      <div style={s.navBar}>
        <input
          style={s.search}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Environment Packs — try forest, haunted, snow, jungle"
          aria-label="Search Environment Packs"
        />
        <div style={s.groupRow}>
          <button
            type="button"
            style={group === 'all' ? s.groupBtnOn : s.groupBtn}
            onClick={() => setGroup('all')}
          >
            All ({INDEXED.length})
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              style={group === g.id ? s.groupBtnOn : s.groupBtn}
              onClick={() => setGroup(g.id)}
            >
              {g.name} ({g.count})
            </button>
          ))}
        </div>
      </div>

      {error ? <div style={s.error}>{error}</div> : null}

      {results.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyTitle}>No Environment Packs match &ldquo;{query}&rdquo;</div>
          <div style={s.emptyBody}>
            Try an environment word such as forest, jungle, snow, haunted or ancient.
          </div>
        </div>
      ) : null}

      {grouped.map((g) => (
        <section key={g.id} style={s.groupSection}>
          <div style={s.groupHead}>
            <h2 style={s.groupTitle}>{g.name}</h2>
            <span style={s.groupCount}>{g.packs.length} pack{g.packs.length === 1 ? '' : 's'}</span>
          </div>
          <div style={s.grid}>
            {g.packs.map((p) => {
              const owned = ownedSlugs.has(p.id);
              return (
                <article key={p.id} style={s.card}>
                  <GuardedCheckoutConsent checkout={consentCheckout} />
                  {/* Fixed-height regions keep every card in the row structurally
                      identical regardless of how long its name runs. */}
                  <div style={s.cardBadgeRow}>
                    {owned ? <span style={s.ownedBadge}>Owned</span> : <span style={s.catBadge}>{p.categoryName}</span>}
                  </div>
                  <div style={s.cardTitle}>{p.name}</div>
                  <div style={s.cardMeta}>
                    {p.generatorCount ? `${p.generatorCount} generators` : ' '}
                  </div>
                  <div style={s.cardPriceRow}>
                    <span style={s.cardPrice}>{formatPrice(env.price_usd, commerce.currency)}</span>
                    <span style={s.cardOneTime}>one-time</span>
                  </div>
                  <div style={s.cardFacts}>
                    <div style={s.fact}><span style={s.dot} />Permanent ownership</div>
                    <div style={s.fact}><span style={s.dot} />{env.included_credits} Creation Credits included</div>
                  </div>
                  <button
                    type="button"
                    style={owned ? s.cardBtnOwned : s.cardBtn}
                    disabled={owned || busy === p.id}
                    onClick={() => (owned ? null : purchase(p.id))}
                  >
                    {owned ? 'Owned' : (busy === p.id ? 'Starting checkout…' : 'Purchase pack')}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ))}
      </div>
      </div>
    </>
  );
}

const s = {
  // Same ground as Pricing so the two commercial pages match.
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #100e09 0%, #17150f 100%)', boxSizing: 'border-box' },
  root: { display: 'flex', flexDirection: 'column', gap: 22, width: '100%', maxWidth: 1240, margin: '0 auto', padding: '28px 20px 60px', boxSizing: 'border-box' },

  intro: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 10 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: AMBER },
  h1: { margin: 0, fontWeight: 800, fontSize: 'clamp(24px, 2.4vw, 32px)', color: '#f4f4f8', lineHeight: 1.15 },
  introBody: { margin: 0, fontSize: 'clamp(14px, 1.1vw, 16px)', color: 'rgba(255,255,255,0.68)', lineHeight: 1.6, maxWidth: 860 },
  strong: { color: '#f0e3bd' },
  introMetaRow: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 },
  metaPill: { fontSize: 12, fontWeight: 700, color: '#f0e3bd', background: 'rgba(212,167,44,0.12)', border: '1px solid rgba(212,167,44,0.3)', borderRadius: 999, padding: '5px 14px' },
  linkBtn: { background: 'transparent', border: 'none', color: AMBER, fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 },

  navBar: { display: 'flex', flexDirection: 'column', gap: 12 },
  search: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(212,167,44,0.35)', borderRadius: 12, padding: '14px 18px', fontSize: 15, color: '#f4f4f8', outline: 'none' },
  groupRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  groupBtn: { background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  groupBtnOn: { background: 'rgba(212,167,44,0.16)', color: '#f0e3bd', border: '1px solid rgba(212,167,44,0.5)', borderRadius: 999, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },

  error: { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.4)', color: '#fca5a5', borderRadius: 10, padding: '12px 16px', fontSize: 14 },
  empty: { background: CARD_BG, border: CARD_BORDER, borderRadius: 16, padding: 28 },
  emptyTitle: { fontWeight: 800, fontSize: 18, color: '#f4f4f8', marginBottom: 6 },
  emptyBody: { fontSize: 14, color: 'rgba(255,255,255,0.6)' },

  groupSection: { display: 'flex', flexDirection: 'column', gap: 12 },
  groupHead: { display: 'flex', alignItems: 'baseline', gap: 10 },
  groupTitle: { margin: 0, fontWeight: 800, fontSize: 'clamp(17px, 1.5vw, 20px)', color: '#f4f4f8' },
  groupCount: { fontSize: 13, color: 'rgba(255,255,255,0.45)' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 16, alignItems: 'stretch' },

  // Uniform internal geometry: every region has a fixed height or grows
  // predictably, and the action is pinned to the bottom with marginTop:auto so
  // buttons align across the row whatever the name length.
  card: { background: viGlassFill('#8A6D14', '#d4a72c', '#E8C65A'), border: viGlassBorder('#E8C65A', 1.5), boxShadow: viGlassShadow('#8A6D14', '#d4a72c', '#E8C65A', false), borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', height: '100%' },
  cardBadgeRow: { height: 24, display: 'flex', alignItems: 'center', marginBottom: 8 },
  ownedBadge: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#7bd88f', background: 'rgba(22,163,74,0.14)', border: '1px solid rgba(22,163,74,0.4)', borderRadius: 6, padding: '3px 8px' },
  catBadge: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardTitle: { fontWeight: 800, fontSize: 16, color: '#f4f4f8', lineHeight: 1.3, minHeight: 42 },
  cardMeta: { fontSize: 12, color: 'rgba(255,255,255,0.5)', height: 18, marginTop: 2 },
  cardPriceRow: { display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10, height: 30 },
  cardPrice: { fontSize: 22, fontWeight: 900, color: AMBER },
  cardOneTime: { fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600 },
  cardFacts: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, minHeight: 44 },
  fact: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'rgba(255,255,255,0.66)' },
  dot: { width: 6, height: 6, borderRadius: '50%', background: AMBER, flexShrink: 0 },
  cardBtn: { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), marginTop: 'auto', borderRadius: 999, padding: '11px 0', fontWeight: 800, fontSize: 13.5, width: '100%', cursor: 'pointer', marginBlockStart: 'auto' },
  cardBtnOwned: { ...glassSurface('#0E7A50', '#16A34A', '#4ADE80'), marginTop: 'auto', borderRadius: 999, padding: '11px 0', fontWeight: 800, fontSize: 13.5, width: '100%', cursor: 'default' },
};
